import React, { useEffect, useRef, useState } from 'react';

import {
  CircularProgress,
  ClickAwayListener,
  makeStyles,
  MenuItem,
  Paper,
  TextField,
  MenuList,
  IconButton,
  Typography,
  InputAdornment,
  FormHelperText,
} from '@material-ui/core';
import Popper from '@material-ui/core/Popper';
import CloseIcon from '@material-ui/icons/Close';
import clsx from 'clsx';
import { get } from 'lodash';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import ensureIsArray from 'helpers/ensureIsArray';
import useApiCall from 'hooks/useApiCall';
import useBoolState from 'hooks/useBoolState';
import useDebounce from 'hooks/useDebounce';
import useLoadingState from 'hooks/useLoadingState';
import general_messages from 'messages/general_messages';

const useStyles = makeStyles(theme => ({
  wrapper: {
    width: '100%',
  },
  error: {
    marginLeft: 14,
    marginRight: 14,
    marginTop: 4,
  },
  popper: {
    zIndex: theme.zIndex.modal,
    maxHeight: '200px',
    overflow: 'scroll',
    boxShadow: theme.shadows[2],
  },
  popperContent: {
    width: '100%',
    display: 'grid',
  },
  popperElement: {
    background: 'none',
    border: 'none',
    display: 'grid',
    gridAutoColumns: '100px auto',
  },
  selectedValues: {
    paddingTop: theme.spacing(2),
  },
  selectedElement: {
    borderBottom: `thin solid ${theme.palette.secondary[500]}`,
    padding: theme.spacing(1),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  noData: {
    padding: theme.spacing(1),
    textAlign: 'center',
  },
}));

const FormAutocomplete = ({
  label,
  formik,
  id,
  apiCallParamsGetter,
  optionsParser,
  color,
  customizeLabel,
  labelClassName,
  disabled,
  setScrollDisabled,
  multiple,
  initialOptions,
  initialOptionsLoading,
}) => {
  const { t } = useTranslation();
  const { state: isPopperOpen, setTrue: openPopper, setFalse: closePopper } = useBoolState(false);
  const ref = useRef(null);

  const [options, setOptions] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const { loading, setLoaded, setLoading } = useLoadingState(!initialOptions?.length);

  const { apiCall, cancelRequest } = useApiCall();
  const debouncedSearchValue = useDebounce(inputValue, 300);
  const { touched, errors, setFieldValue, values } = formik;
  const error = get(errors, id);

  const selectedValues = get(values, id) || [];
  const filteredInitialOptions = (initialOptions || []).filter(opt => !selectedValues.some(s => s.key === opt.key));
  let displayOptions;
  if (inputValue) {
    displayOptions = options;
  } else if (initialOptionsLoading) {
    displayOptions = [];
  } else {
    displayOptions = filteredInitialOptions;
  }
  const showInputLoader = (loading && isPopperOpen) || (initialOptionsLoading && !initialOptions?.length && isPopperOpen);

  const updateOptions = optionsToSet => {
    if (optionsToSet) {
      const parsedOptions = optionsParser(optionsToSet);
      const currentSelected = get(values, id) || [];
      const filteredOptions = parsedOptions.filter(({ key }) => !currentSelected.some(selectedValue => selectedValue.key === key));
      setOptions(filteredOptions);
    }
  };

  const performSearch = async value => {
    cancelRequest();
    if (apiCallParamsGetter) {
      const result = await apiCall(...ensureIsArray(apiCallParamsGetter(value)));
      // Handle both direct data and response with data property
      const data = result?.data || result;
      updateOptions(data);
      setLoaded();
    }
  };

  useEffect(() => {
    if (debouncedSearchValue) {
      setLoading();
      performSearch(debouncedSearchValue);
    }
  }, [debouncedSearchValue]);

  useEffect(() => {
    if (initialOptions?.length && !initialOptionsLoading) {
      setLoaded();
    }
  }, [initialOptions, initialOptionsLoading, setLoaded]);

  const onInputChange = ({ target }) => {
    setInputValue(target.value);
    openPopper();
    setScrollDisabled(true);
  };

  const onInputFocus = ({ target }) => {
    if (target.value) openPopper();
    setScrollDisabled(true);
  };

  const onSelect = (event, value) => {
    const newValue = [value];
    if (multiple) {
      const previousValue = get(values, id) || [];
      newValue.unshift(...previousValue);
    }
    setFieldValue(id, newValue);
    closePopper();
    setOptions([]);
    setInputValue('');
    setScrollDisabled(false);
  };

  const onRemove = key => {
    const previousValue = get(values, id) || [];
    setFieldValue(
      id,
      previousValue.filter(value => value.key !== key),
    );
  };

  const onClickAway = () => {
    closePopper();
    setScrollDisabled(false);
  };

  const styles = useStyles();

  const showPopperLoader = (loading || initialOptionsLoading) && displayOptions.length === 0;
  let popperContent;
  if (showPopperLoader) {
    popperContent = (
      <Paper className={styles.popperContent} elevation={3}>
        <div className={styles.noData} style={{ width: ref?.current?.offsetWidth }}>
          <CircularProgress color='secondary' size={24} />
        </div>
      </Paper>
    );
  } else if (displayOptions.length) {
    popperContent = (
      <Paper className={styles.popperContent} elevation={3}>
        <MenuList style={{ width: ref?.current?.offsetWidth }}>
          {displayOptions.map(option => (
            <MenuItem
              key={option.key}
              className={clsx(styles.popperElement, labelClassName)}
              data-testid={`${option.id}-dropdown`}
              onClick={e => onSelect(e, option)}
            >
              {customizeLabel(option, t)}
            </MenuItem>
          ))}
        </MenuList>
      </Paper>
    );
  } else {
    popperContent = (
      <Paper className={styles.popperContent} elevation={3}>
        <Typography className={styles.noData} style={{ width: ref?.current?.offsetWidth }}>
          {t(...general_messages.no_data)}
        </Typography>
      </Paper>
    );
  }

  return (
    <div>
      <ClickAwayListener onClickAway={onClickAway}>
        <div className={styles.wrapper}>
          <TextField
            color={color}
            data-testid={`${id}-autocomplete`}
            disabled={disabled}
            error={touched[id] && Boolean(error)}
            fullWidth
            innerRef={ref}
            InputProps={{
              endAdornment: showInputLoader ? (
                <InputAdornment position='end'>
                  <CircularProgress color='secondary' size={20} />
                </InputAdornment>
              ) : null,
            }}
            label={label}
            onChange={onInputChange}
            onFocus={onInputFocus}
            value={inputValue}
            variant='outlined'
          />
          <FormHelperText className={styles.error} error>
            {error && error}
          </FormHelperText>
          <Popper
            anchorEl={ref.current}
            className={styles.popper}
            open={isPopperOpen && (!loading || (initialOptionsLoading && displayOptions.length === 0))}
          >
            {popperContent}
          </Popper>
        </div>
      </ClickAwayListener>

      {get(values, id)?.length > 0 && (
        <div className={styles.selectedValues}>
          {get(values, id).map(option => (
            <div key={option.key} className={styles.selectedElement}>
              <div className={labelClassName}>{customizeLabel(option, t)}</div>
              {!disabled && (
                <IconButton onClick={() => onRemove(option.key)} size='small'>
                  <CloseIcon />
                </IconButton>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

FormAutocomplete.propTypes = {
  formik: PropTypes.shape({
    touched: PropTypes.shape({}),
    errors: PropTypes.shape({}),
    handleChange: PropTypes.func,
    handleBlur: PropTypes.func,
    setFieldValue: PropTypes.func,
    values: PropTypes.object.isRequired,
  }).isRequired,
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  apiCallParamsGetter: PropTypes.func.isRequired,
  optionsParser: PropTypes.func,
  customizeLabel: PropTypes.func,
  color: PropTypes.string,
  labelClassName: PropTypes.string,
  disabled: PropTypes.bool,
  setScrollDisabled: PropTypes.func,
  multiple: PropTypes.bool,
  initialOptions: PropTypes.arrayOf(
    PropTypes.shape({ key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), fullName: PropTypes.string }),
  ),
  initialOptionsLoading: PropTypes.bool,
};

FormAutocomplete.defaultProps = {
  customizeLabel: option => option.label,
  optionsParser: data => data,
  color: 'primary',
  labelClassName: null,
  disabled: false,
  setScrollDisabled: () => null,
  multiple: true,
  initialOptions: [],
  initialOptionsLoading: false,
};

export default FormAutocomplete;
